import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useEmblaCarousel from 'embla-carousel-react';
import cn from 'classnames';

import Heading from 'components/shared/heading';
import styles from './styles.module.scss';
import ReviewCard from './components/ReviewCard';

const CustomerReviews = ({
  title = undefined,
  subTitle = undefined,
  helpText = undefined,
  writeReviewLink = undefined,
  reviewsTabOneLabel,
  reviewsTabOne,
  reviewsTabTwoLabel,
  reviewsTabThreeLabel,
  reviewsTabThree,
  reviewsTabTwo,
}) => {
  const [activeTab, setActiveTab] = useState(1);

  const onTabPress = useCallback(
    (tab) => () => {
      setActiveTab(tab);
    },
    []
  );

  const items = useMemo(() => {
    switch (activeTab) {
      case 1:
        return reviewsTabOne;
      case 2:
        return reviewsTabTwo;
      case 3:
        return reviewsTabThree;
      default:
        return [];
    }
  }, [activeTab, reviewsTabOne, reviewsTabTwo, reviewsTabThree]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    skipSnaps: false,
    duration: 25,
  });

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (emblaApi && items?.length) {
      emblaApi.reInit();
    }
  }, [emblaApi, items]);

  return (
    <div className="container">
      <div className={styles.customerReviews}>
        <div className={styles.info}>
          {title && (
            <Heading className={styles.title} tag="h2" size="xl">
              {title}
            </Heading>
          )}
          {helpText && <p className={styles.helpTxt}>{helpText}</p>}
          <div className={styles.tabs}>
            {reviewsTabOneLabel && reviewsTabOne?.length > 0 && (
              <button
                className={cn({ [styles.active]: activeTab === 1 })}
                type="button"
                onClick={onTabPress(1)}
              >
                {reviewsTabOneLabel}
              </button>
            )}
            {reviewsTabTwoLabel && reviewsTabTwo?.length > 0 && (
              <button
                className={cn({ [styles.active]: activeTab === 2 })}
                type="button"
                onClick={onTabPress(2)}
              >
                {reviewsTabTwoLabel}
              </button>
            )}
            {reviewsTabThreeLabel && reviewsTabThree?.length > 0 && (
              <button
                className={cn({ [styles.active]: activeTab === 3 })}
                type="button"
                onClick={onTabPress(3)}
              >
                {reviewsTabThreeLabel}
              </button>
            )}
          </div>
          {subTitle && <h5 className={styles.subTitle}>{subTitle}</h5>}
        </div>

        <div className={styles.slider}>
          {items?.length > 0 && (
            <div className={styles.emblaWrapper}>
              <button
                type="button"
                className={cn(styles.emblaButton, styles.emblaButtonPrev, {
                  [styles.emblaButtonDisabled]: !prevBtnEnabled,
                })}
                onClick={scrollPrev}
                disabled={!prevBtnEnabled}
                aria-label="Previous reviews"
              />
              <div className={styles.embla} ref={emblaRef}>
                <div className={styles.emblaContainer}>
                  {items.map((item, index) => (
                    <div className={styles.reviewItem} key={`cr-${index}`}>
                      <ReviewCard {...item} />
                    </div>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className={cn(styles.emblaButton, styles.emblaButtonNext, {
                  [styles.emblaButtonDisabled]: !nextBtnEnabled,
                })}
                onClick={scrollNext}
                disabled={!nextBtnEnabled}
                aria-label="Next reviews"
              />
            </div>
          )}
        </div>
        {writeReviewLink && (
          <div className={styles.linkBox}>
            <a href={writeReviewLink} target="_blank" rel="noopener noreferrer">
              Write a review
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

CustomerReviews.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  writeReviewLink: PropTypes.string,
  helpText: PropTypes.string,
  reviewsTabOneLabel: PropTypes.string.isRequired,
  reviewsTabOne: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      title: PropTypes.string,
      photo: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
        sizes: PropTypes.shape({
          thumbnail: PropTypes.shape({
            url: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        }),
      }),
      rate: PropTypes.number.isRequired,
      feedbackText: PropTypes.string.isRequired,
      textTitle: PropTypes.string,
      address: PropTypes.string,
    })
  ).isRequired,
  reviewsTabTwoLabel: PropTypes.string.isRequired,
  reviewsTabTwo: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      title: PropTypes.string,
      photo: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
        sizes: PropTypes.shape({
          thumbnail: PropTypes.shape({
            url: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        }),
      }),
      rate: PropTypes.number.isRequired,
      feedbackText: PropTypes.string.isRequired,
      textTitle: PropTypes.string,
      address: PropTypes.string,
    })
  ).isRequired,
  reviewsTabThreeLabel: PropTypes.string.isRequired,
  reviewsTabThree: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      title: PropTypes.string,
      photo: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
        sizes: PropTypes.shape({
          thumbnail: PropTypes.shape({
            url: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
          }),
        }),
      }),
      rate: PropTypes.number.isRequired,
      feedbackText: PropTypes.string.isRequired,
      textTitle: PropTypes.string,
      address: PropTypes.string,
    })
  ).isRequired,
  // background: PropTypes.oneOf(['white', 'darkblue']),
};

export default CustomerReviews;
